import { SubscriptionAddon, SubscriptionModule, SubscriptionType } from "@/constants/workplaces";
import { getAddonParentModule, getSocAddons } from "@coronet/cui";
import { i18n } from "@/plugins/i18n";
import router from "@/_helpers/router";
import { RouteName } from "@/constants/routes";
import { useWorkspaceStore } from "@/_store/workspace.module";

/**
 * Checks if a subscription module is disabled for the current workspace.
 * Returns `true` when the subscription or modules map is absent (safe default).
 * @sideEffect reads `useWorkspaceStore`
 */
export const isModuleDisabled = (moduleName: SubscriptionModule): boolean => {
  const workspaceStore = useWorkspaceStore();
  const modules = workspaceStore.workspace.subscription?.modules;
  if (!modules) {
    return true;
  }
  return modules[moduleName] ? !modules[moduleName].enabled : true;
};

/**
 * Checks if a subscription addon is disabled.
 * An addon is considered disabled when its parent module is absent or disabled,
 * or when the addon itself is not found / not enabled.
 * @sideEffect reads `useWorkspaceStore`
 */
export const isAddonDisabled = (addonName: SubscriptionAddon): boolean => {
  const workspaceStore = useWorkspaceStore();
  const modules = workspaceStore.workspace.subscription?.modules;
  if (!modules) {
    return true;
  }
  const addonModuleName = getAddonParentModule(addonName);
  if (!addonModuleName || !modules[addonModuleName] || isModuleDisabled(addonModuleName)) {
    return true;
  }
  const addon = modules[addonModuleName].addons.find((v) => v.name === addonName);
  return addon ? !addon.enabled : true;
};

/**
 * Returns `true` when the current workspace subscription type is `FREEZE`.
 * @sideEffect reads `useWorkspaceStore`
 */
export const isWorkspaceFrozen = () => {
  const workspaceStore = useWorkspaceStore();
  return workspaceStore.workspace.subscriptionType === SubscriptionType.FREEZE;
};

/**
 * Returns `true` when the workspace subscription type is `INITIAL`
 * (newly provisioned workspace, not yet fully set up).
 * @sideEffect reads `useWorkspaceStore`
 */
export const isWorkspaceInitial = () => {
  const workspaceStore = useWorkspaceStore();
  return workspaceStore.workspace.subscriptionType === SubscriptionType.INITIAL;
};

/**
 * Returns `true` when the workspace is a newly-created workspace currently in demo mode
 * (subscription type `INITIAL`, demo mode enabled, and a demo end date present).
 * @sideEffect reads `useWorkspaceStore`
 */
export const isWorkspaceNewAndInDemoMode = () => {
  const workspaceStore = useWorkspaceStore();
  const { demoModeEndDate, demoModeEnabled, subscriptionType } = workspaceStore.workspace;
  return Boolean(
    subscriptionType === SubscriptionType.INITIAL && demoModeEnabled && demoModeEndDate
  );
};

/**
 * @deprecated Use {@link useAppsManagementModule} (from `apps-mgmt.module.ts`) and check
 * its `context` property to determine the current context instead.
 * @sideEffect reads `router.currentRoute`
 */
export function isMspPortalRoute() {
  const route = router.currentRoute.value;
  const rootRoute = route.matched[0]?.name;
  return rootRoute === RouteName.MSP;
}

/**
 * Returns the disclaimer text for a dialog action button.
 * Falls back to {@link getDefaultDisclaimerText} when the action is explicitly
 * disabled or the workspace is frozen.
 * @sideEffect reads `useWorkspaceStore` (via `isWorkspaceFrozen`)
 */
export function getDisclaimerText(disable: boolean = false, disclaimer: string = ""): string {
  if (disable || isWorkspaceFrozen()) {
    return getDefaultDisclaimerText();
  }
  return disclaimer;
}

/**
 * Returns the appropriate default disclaimer i18n key based on workspace state:
 * - Frozen workspace on a non-MSP route → `general.subscriptionExpired`
 * - All other cases → `general.noPermissions`
 * @sideEffect reads `useWorkspaceStore` and `router.currentRoute`
 */
/**
 * Returns `true` when at least one SOC-family addon (the canonical
 * {@link getSocAddons} set) is currently enabled for the active workspace.
 * @sideEffect reads `useWorkspaceStore` (via `isAddonDisabled`)
 */
export const socAddonsEnabled = (): boolean => {
  return getSocAddons().some((addon) => !isAddonDisabled(addon));
};

/**
 * Returns the appropriate default disclaimer i18n key based on workspace state:
 * - Frozen workspace on a non-MSP route → `general.subscriptionExpired`
 * - All other cases → `general.noPermissions`
 * @sideEffect reads `useWorkspaceStore` and `router.currentRoute`
 */
export function getDefaultDisclaimerText() {
  const mspRoute = isMspPortalRoute();
  if (isWorkspaceFrozen() && !mspRoute) {
    return i18n.global.t("general.subscriptionExpired");
  }
  return i18n.global.t("general.noPermissions");
}
